// ============================================================
// Datei: frontend/src/pages/Journal.tsx
// Trading Engine v2.2 — Bilder, Tags, Suchleiste
// ============================================================
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { journalApi, tradesApi, accountsApi, JournalEntry, Trade, Account } from '../services/api';
import { useToast } from '../components/Toast';

const s = {
  page: { padding: 0 } as React.CSSProperties,
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 } as React.CSSProperties,
  title: { fontSize: 28, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 12 } as React.CSSProperties,
  badge: { background: '#22c55e', color: '#000', fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20 } as React.CSSProperties,
  count: { color: '#6b7280', fontSize: 14, fontWeight: 400, marginLeft: 8 } as React.CSSProperties,
  btnPrimary: { background: '#f97316', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 } as React.CSSProperties,
  filterRow: { display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' } as React.CSSProperties,
  searchInput: { background: '#0d1117', color: '#d1d5db', border: '1px solid #1e2539', padding: '8px 14px', borderRadius: 8, fontSize: 14, flex: 1 } as React.CSSProperties,
  select: { background: '#0d1117', color: '#d1d5db', border: '1px solid #1e2539', padding: '8px 14px', borderRadius: 8, fontSize: 14, minWidth: 160 } as React.CSSProperties,
  entryCard: { background: '#13172b', border: '1px solid #1e2539', borderRadius: 14, padding: 20, marginBottom: 14, transition: 'border-color 0.2s' } as React.CSSProperties,
  entryHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer' } as React.CSSProperties,
  entryTitle: { fontSize: 16, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 } as React.CSSProperties,
  entryMeta: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, flexWrap: 'wrap' as const } as React.CSSProperties,
  metaText: { color: '#6b7280', fontSize: 12 } as React.CSSProperties,
  linkedTrade: { background: '#22c55e22', color: '#22c55e', padding: '1px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 } as React.CSSProperties,
  tag: { background: '#8b5cf622', color: '#a78bfa', padding: '1px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 } as React.CSSProperties,
  actionBtns: { display: 'flex', gap: 8, flexShrink: 0 } as React.CSSProperties,
  actionBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '4px' } as React.CSSProperties,
  entryContent: { color: '#d1d5db', fontSize: 14, lineHeight: 1.6, marginTop: 14, paddingTop: 14, borderTop: '1px solid #1e253966' } as React.CSSProperties,
  // Images
  imageGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginTop: 12 } as React.CSSProperties,
  imageThumb: { width: '100%', height: 140, objectFit: 'cover' as const, borderRadius: 8, border: '1px solid #1e2539', cursor: 'pointer' } as React.CSSProperties,
  imageOverlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' } as React.CSSProperties,
  imageFull: { maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12 } as React.CSSProperties,
  // Modal
  overlay: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' } as React.CSSProperties,
  modal: { background: '#13172b', border: '1px solid #1e2539', borderRadius: 14, padding: 28, width: '90%', maxWidth: 700, maxHeight: '90vh', overflow: 'auto' } as React.CSSProperties,
  modalTitle: { color: '#fff', fontSize: 20, fontWeight: 700, marginBottom: 20 } as React.CSSProperties,
  formGroup: { display: 'flex', flexDirection: 'column' as const, gap: 4, marginBottom: 14 } as React.CSSProperties,
  label: { color: '#9ca3af', fontSize: 12, fontWeight: 600 } as React.CSSProperties,
  input: { background: '#0d1117', color: '#fff', border: '1px solid #1e2539', padding: '10px 14px', borderRadius: 8, fontSize: 14, outline: 'none' } as React.CSSProperties,
  textarea: { background: '#0d1117', color: '#fff', border: '1px solid #1e2539', padding: '10px 14px', borderRadius: 8, fontSize: 14, outline: 'none', minHeight: 120, resize: 'vertical' as const } as React.CSSProperties,
  btnRow: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 } as React.CSSProperties,
  btnCancel: { background: '#1e2539', color: '#9ca3af', border: '1px solid #333', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 14 } as React.CSSProperties,
  tagContainer: { display: 'flex', flexWrap: 'wrap' as const, gap: 6, marginTop: 4 } as React.CSSProperties,
  tagBadge: { background: '#8b5cf622', color: '#a78bfa', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 } as React.CSSProperties,
  tagRemove: { cursor: 'pointer', fontWeight: 700, fontSize: 14, lineHeight: 1 } as React.CSSProperties,
  tagInput: { background: '#0d1117', color: '#fff', border: '1px solid #1e2539', padding: '6px 10px', borderRadius: 8, fontSize: 13, minWidth: 120 } as React.CSSProperties,
  section: { background: '#0d111766', border: '1px solid #1e253966', borderRadius: 8, padding: 14, marginBottom: 14 } as React.CSSProperties,
  sectionLabel: { color: '#9ca3af', fontSize: 12, fontWeight: 600, marginBottom: 8 } as React.CSSProperties,
  uploadArea: { border: '2px dashed #1e2539', borderRadius: 10, padding: 20, textAlign: 'center' as const, cursor: 'pointer', transition: 'border-color 0.2s', color: '#6b7280' } as React.CSSProperties,
  uploadedImgSmall: { width: 80, height: 80, objectFit: 'cover' as const, borderRadius: 6, border: '1px solid #1e2539' } as React.CSSProperties,
};

const Journal: React.FC = () => {
  const toast = useToast();
  // Safe date formatter
  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '–';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr.split('T')[0] || '–';
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return '–'; }
  };

  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [activeAccount, setActiveAccount] = useState<Account | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTag, setFilterTag] = useState('');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState<JournalEntry | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formTradeId, setFormTradeId] = useState<string>('');
  const [formTags, setFormTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  // Image
  const [viewImage, setViewImage] = useState<string | null>(null);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  // Helper: File → Base64 Data URL
  const fileToDataUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const loadData = useCallback(async () => {
    try {
      const accRes = await accountsApi.list();
      const active = accRes.data.find(a => a.is_active);
      if (active) {
        setActiveAccount(active);
        const params: any = { account_id: active.id };
        if (filterTag) params.tag = filterTag;
        if (searchQuery) params.search = searchQuery;
        const jRes = await journalApi.list(params);
        setEntries(jRes.data);
        const tRes = await tradesApi.list({ account_id: active.id });
        setTrades(tRes.data);
        const tagRes = await journalApi.getTags(active.id);
        setAllTags(tagRes.data);
      }
    } catch (e) { console.error(e); }
  }, [filterTag, searchQuery]);

  useEffect(() => { loadData(); }, [loadData]);

  // Tags
  const addTag = () => {
    const t = tagInput.trim().replace(/^#/, '');
    if (t && !formTags.includes(t)) setFormTags([...formTags, t]);
    setTagInput('');
  };
  const removeTag = (tag: string) => setFormTags(formTags.filter(t => t !== tag));

  // Modal
  const openNew = () => {
    setEditEntry(null);
    setFormTitle(''); setFormContent(''); setFormTradeId(''); setFormTags([]);
    setUploadFiles([]);
    setShowModal(true);
  };

  const openEdit = (entry: JournalEntry) => {
    setEditEntry(entry);
    setFormTitle(entry.title);
    setFormContent(entry.content);
    setFormTradeId(entry.trade_id || '');
    setFormTags(entry.tags || []);
    setUploadFiles([]);
    setShowModal(true);
  };

  const saveEntry = async () => {
    if (!activeAccount) return;
    try {
      if (editEntry) {
        await journalApi.update(editEntry.id, {
          title: formTitle,
          content: formContent,
          tags: formTags,
        });
        // Upload neuer Bilder
        for (const file of uploadFiles) {
          const dataUrl = await fileToDataUrl(file);
          await journalApi.uploadImage(editEntry.id, dataUrl);
        }
      } else {
        const res = await journalApi.create({
          account_id: activeAccount.id,
          trade_id: formTradeId || null,
          title: formTitle,
          content: formContent,
          tags: formTags,
        });
        // Upload Bilder
        for (const file of uploadFiles) {
          const dataUrl = await fileToDataUrl(file);
          await journalApi.uploadImage(res.data.id, dataUrl);
        }
      }
      toast.success(editEntry ? 'Entry updated!' : 'Journal entry created!');
      setShowModal(false);
      loadData();
    } catch (e: any) {
      toast.error('Error saving', e.response?.data || e.message);
    }
  };

  const deleteEntry = async (id: string) => {
    if (window.confirm('Delete entry?')) {
      await journalApi.delete(id);
      toast.success('Entry deleted');
      loadData();
    }
  };

  const removeImage = async (entryId: string, imageUrl: string) => {
    if (window.confirm('Remove image?')) {
      await journalApi.removeImage(entryId, imageUrl);
      loadData();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUploadFiles([...uploadFiles, ...Array.from(e.target.files)]);
    }
  };

  const getLinkedTrade = (tradeId: string | null): Trade | undefined => {
    return trades.find(t => t.id === tradeId);
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.title}>
          📓 Trading Journal
          {activeAccount && <span style={s.badge}>{activeAccount.name}</span>}
          <span style={s.count}>{entries.length} Entries</span>
        </div>
        <button style={s.btnPrimary} onClick={openNew}>+ New Entry</button>
      </div>

      {/* Filter + Search */}
      <div style={s.filterRow}>
        <input
          style={s.searchInput}
          placeholder="🔍 Search in title, content, tags..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && loadData()}
        />
        <select style={s.select} value={filterTag} onChange={e => setFilterTag(e.target.value)}>
          <option value="">Alle Entries</option>
          {allTags.map(t => <option key={t} value={t}>#{t}</option>)}
        </select>
      </div>

      {/* Entries */}
      {entries.map(entry => {
        const isExpanded = expandedId === entry.id;
        const linkedTrade = getLinkedTrade(entry.trade_id);
        return (
          <div key={entry.id} style={{ ...s.entryCard, borderColor: isExpanded ? '#3b82f6' : '#1e2539' }}>
            <div style={s.entryHeader} onClick={() => setExpandedId(isExpanded ? null : entry.id)}>
              <div>
                <div style={s.entryTitle}>
                  <span>{isExpanded ? '▼' : '▶'}</span>
                  {entry.title}
                </div>
                <div style={s.entryMeta}>
                  <span style={s.metaText}>
                    {new Date(entry.created_at).toLocaleDateString('de-DE')}, {new Date(entry.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {linkedTrade && (
                    <span style={s.linkedTrade}>
                      📎 {linkedTrade.asset} {linkedTrade.pnl >= 0 ? '+' : ''}${linkedTrade.pnl.toFixed(2)}
                    </span>
                  )}
                  {(entry.tags || []).map(t => (
                    <span key={t} style={s.tag}>#{t}</span>
                  ))}
                  {entry.images.length > 0 && (
                    <span style={{ ...s.metaText, color: '#60a5fa' }}>📷 {entry.images.length}</span>
                  )}
                </div>
              </div>
              <div style={s.actionBtns}>
                <button style={s.actionBtn} title="Bearbeiten" onClick={(e) => { e.stopPropagation(); openEdit(entry); }}>✏️</button>
                <button style={s.actionBtn} title="Delete" onClick={(e) => { e.stopPropagation(); deleteEntry(entry.id); }}>🗑️</button>
              </div>
            </div>

            {isExpanded && (
              <div style={s.entryContent}>
                {entry.content || <span style={{ color: '#4b5563', fontStyle: 'italic' }}>No content</span>}

                {/* Images */}
                {entry.images.length > 0 && (
                  <div style={s.imageGrid}>
                    {entry.images.map((img, idx) => (
                      <div key={idx} style={{ position: 'relative' }}>
                        <img
                          src={img.startsWith('http') ? img : `http://localhost:8080${img}`}
                          alt={`Bild ${idx + 1}`}
                          style={s.imageThumb}
                          onClick={() => setViewImage(img.startsWith('http') ? img : `http://localhost:8080${img}`)}
                        />
                        <button
                          style={{ position: 'absolute', top: 4, right: 4, background: '#ef444488', border: 'none', borderRadius: '50%', width: 22, height: 22, color: '#fff', cursor: 'pointer', fontSize: 12 }}
                          onClick={() => removeImage(entry.id, img)}
                          title="Bild entfernen"
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {entries.length === 0 && (
        <div style={{ textAlign: 'center', color: '#6b7280', padding: 60 }}>
          No journal entries found
        </div>
      )}

      {/* Image Fullscreen Viewer */}
      {viewImage && (
        <div style={s.imageOverlay} onClick={() => setViewImage(null)}>
          <img src={viewImage} alt="Vollbild" style={s.imageFull} />
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" style={s.overlay} onClick={() => setShowModal(false)}>
          <div className="modal-card" style={s.modal} onClick={e => e.stopPropagation()}>
            <h2 style={s.modalTitle}>{editEntry ? '✏️ Edit Entry' : '📓 New Entry'}</h2>

            <div style={s.formGroup}>
              <label style={s.label}>Title *</label>
              <input style={s.input} value={formTitle} onChange={e => setFormTitle(e.target.value)}
                     placeholder="Entry title" />
            </div>

            <div style={s.formGroup}>
              <label style={s.label}>Linked Trade</label>
              <select style={s.input} value={formTradeId} onChange={e => setFormTradeId(e.target.value)}>
                <option value="">– No Trade –</option>
                {trades.map(t => (
                  <option key={t.id} value={t.id}>
                    {formatDate(t.trade_date)} – {t.asset} ({t.pnl >= 0 ? '+' : ''}${t.pnl.toFixed(2)})
                  </option>
                ))}
              </select>
            </div>

            <div style={s.formGroup}>
              <label style={s.label}>Content</label>
              <textarea style={s.textarea} value={formContent} onChange={e => setFormContent(e.target.value)}
                        placeholder="Your analysis, thoughts, learnings..." />
            </div>

            {/* Tags */}
            <div style={s.section}>
              <div style={s.sectionLabel}>🏷️ Tags / Categories</div>
              <div style={s.tagContainer}>
                {formTags.map(t => (
                  <span key={t} style={s.tagBadge}>
                    #{t}
                    <span style={s.tagRemove} onClick={() => removeTag(t)}>×</span>
                  </span>
                ))}
                <input
                  style={s.tagInput}
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); }
                  }}
                  placeholder="#Tag..."
                  list="journal-tag-suggestions"
                />
                <datalist id="journal-tag-suggestions">
                  {allTags.filter(t => !formTags.includes(t)).map(t => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
              </div>
            </div>

            {/* Image Upload */}
            <div style={s.section}>
              <div style={s.sectionLabel}>📷 Images</div>

              {/* Existing images (edit mode) */}
              {editEntry && editEntry.images.length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  {editEntry.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={img.startsWith('http') ? img : `http://localhost:8080${img}`}
                      alt={`Bild ${idx + 1}`}
                      style={s.uploadedImgSmall}
                    />
                  ))}
                </div>
              )}

              {/* New file uploads */}
              {uploadFiles.length > 0 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  {uploadFiles.map((file, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        style={s.uploadedImgSmall}
                      />
                      <button
                        style={{ position: 'absolute', top: -4, right: -4, background: '#ef4444', border: 'none', borderRadius: '50%', width: 18, height: 18, color: '#fff', cursor: 'pointer', fontSize: 10 }}
                        onClick={() => setUploadFiles(uploadFiles.filter((_, i) => i !== idx))}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}

              <div
                style={s.uploadArea}
                onClick={() => fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#3b82f6'; }}
                onDragLeave={e => { e.currentTarget.style.borderColor = '#1e2539'; }}
                onDrop={e => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = '#1e2539';
                  if (e.dataTransfer.files) {
                    setUploadFiles([...uploadFiles, ...Array.from(e.dataTransfer.files)]);
                  }
                }}
              >
                <div style={{ fontSize: 28, marginBottom: 6 }}>📁</div>
                <div>Click or drag images here</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>PNG, JPG, GIF up to 10MB</div>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </div>

            {/* Buttons */}
            <div style={s.btnRow}>
              <button style={s.btnCancel} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={s.btnPrimary} onClick={saveEntry}>
                💾 {editEntry ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Journal;